'use client'

import Link from 'next/link'
import { useState } from 'react'
import { CheckCircle2, AlertTriangle, XCircle, ShieldCheck } from 'lucide-react'
import { LandingShell, StoreButton } from '@/components/landing/ExtensionLanding'
import { DesktopLinkForm } from '@/components/landing/DesktopLinkForm'
import { track } from '@/lib/analytics'
import { checkStatement, type StatementReport, type CheckStatus } from '@/lib/supportingStatementCheck'

// Free tool: checks a supporting statement against the person specification,
// with plain rules in the browser (lib/supportingStatementCheck.ts). Nothing
// typed here is sent anywhere.

const STATUS_STYLE: Record<CheckStatus, { icon: typeof CheckCircle2; colour: string; label: string }> = {
  good: { icon: CheckCircle2, colour: 'text-green-600', label: 'Looks good' },
  warn: { icon: AlertTriangle, colour: 'text-amber-500', label: 'Worth a look' },
  bad: { icon: XCircle, colour: 'text-red-600', label: 'Needs work' },
}

const CRITERION_STYLE = {
  covered: 'bg-green-100 text-green-800',
  partly: 'bg-amber-100 text-amber-800',
  missing: 'bg-red-100 text-red-800',
} as const

const CRITERION_LABEL = { covered: 'Covered', partly: 'Partly', missing: 'Not mentioned' } as const

export default function SupportingStatementCheckerClient() {
  const [jobType, setJobType] = useState<'nhs' | 'other'>('nhs')
  const [personSpec, setPersonSpec] = useState('')
  const [statement, setStatement] = useState('')
  const [limit, setLimit] = useState('')
  const [limitUnit, setLimitUnit] = useState<'words' | 'characters'>('words')
  const [report, setReport] = useState<StatementReport | null>(null)

  const run = (e: React.FormEvent) => {
    e.preventDefault()
    if (!statement.trim()) return
    const parsedLimit = parseInt(limit.replace(/[^\d]/g, ''), 10)
    const result = checkStatement({
      statement,
      personSpec,
      limit: Number.isFinite(parsedLimit) ? parsedLimit : undefined,
      limitUnit,
      nhs: jobType === 'nhs',
    })
    setReport(result)
    track('statement_check', { jobType, hasSpec: !!personSpec.trim(), hasLimit: Number.isFinite(parsedLimit) })
    requestAnimationFrame(() => document.getElementById('results')?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
  }

  const goodCount = report?.checks.filter((c) => c.status === 'good').length ?? 0

  return (
    <LandingShell>
      <section className="bg-gradient-to-b from-primary-50 via-white to-white">
        <div className="container mx-auto px-4 py-10 sm:py-14">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center rounded-full border border-primary-200/60 bg-white/80 px-4 py-2 text-sm font-semibold text-primary-700 shadow-sm mb-5">
              Free tool · NHS and public sector applications
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">Supporting statement checker</h1>
            <p className="mt-4 text-lg text-gray-600">
              Paste your supporting statement and the job&apos;s person specification. See what a shortlister will see: criteria you haven&apos;t covered, examples without results, stock phrases, and whether it fits the limit.
            </p>
            <p className="mt-3 inline-flex items-center gap-2 text-sm text-gray-500">
              <ShieldCheck className="w-4 h-4 text-green-600" /> It runs in your browser. Your text isn&apos;t sent anywhere.
            </p>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 pb-12">
        <form onSubmit={run} className="max-w-3xl mx-auto rounded-2xl border border-gray-200 bg-white p-5 sm:p-8 shadow-sm space-y-6">
          <fieldset>
            <legend className="text-sm font-semibold text-gray-900">What kind of job is it?</legend>
            <div className="mt-2 flex flex-wrap gap-3">
              {([['nhs', 'NHS'], ['other', 'Council, school, charity or other']] as const).map(([value, label]) => (
                <label key={value} className={`cursor-pointer rounded-lg border px-4 py-2 text-sm font-medium ${jobType === value ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-300 text-gray-700'}`}>
                  <input type="radio" name="jobType" value={value} checked={jobType === value} onChange={() => setJobType(value)} className="sr-only" />
                  {label}
                </label>
              ))}
            </div>
          </fieldset>

          <div>
            <label htmlFor="person-spec" className="text-sm font-semibold text-gray-900">Person specification <span className="font-normal text-gray-500">(recommended)</span></label>
            <p className="text-xs text-gray-500 mt-0.5">Copy the criteria from the job&apos;s person specification, one per line. Headings like &quot;Essential&quot; are ignored.</p>
            <textarea
              id="person-spec"
              value={personSpec}
              onChange={(e) => setPersonSpec(e.target.value)}
              rows={7}
              placeholder={'Registered Nurse with current NMC registration\nAbility to recognise and escalate a deteriorating patient\nEffective communication with patients, families and colleagues'}
              className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </div>

          <div>
            <label htmlFor="statement" className="text-sm font-semibold text-gray-900">Your supporting statement</label>
            <textarea
              id="statement"
              value={statement}
              onChange={(e) => setStatement(e.target.value)}
              rows={12}
              required
              placeholder="Paste your statement here, with a blank line between paragraphs."
              className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label htmlFor="limit" className="text-sm font-semibold text-gray-900">Limit on the form <span className="font-normal text-gray-500">(optional)</span></label>
              <input
                id="limit"
                inputMode="numeric"
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
                placeholder="e.g. 1500"
                className="mt-2 block w-36 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </div>
            <div>
              <label htmlFor="limit-unit" className="sr-only">Limit unit</label>
              <select
                id="limit-unit"
                value={limitUnit}
                onChange={(e) => setLimitUnit(e.target.value as 'words' | 'characters')}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900"
              >
                <option value="words">words</option>
                <option value="characters">characters</option>
              </select>
            </div>
            <button type="submit" disabled={!statement.trim()} className="ml-auto rounded-lg bg-primary-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50">
              Check my statement
            </button>
          </div>
        </form>

        {report && (
          <div id="results" className="max-w-3xl mx-auto mt-8 space-y-6 scroll-mt-24">
            <div className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-8 shadow-sm">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-xl font-bold text-gray-900">{goodCount} of {report.checks.length} checks look good</h2>
                <p className="text-sm text-gray-500">{report.words.toLocaleString('en-GB')} words · {report.characters.toLocaleString('en-GB')} characters</p>
              </div>
              <ul className="mt-5 space-y-4">
                {report.checks.map((check) => {
                  const style = STATUS_STYLE[check.status]
                  const Icon = style.icon
                  return (
                    <li key={check.id} className="flex gap-3">
                      <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${style.colour}`} aria-label={style.label} />
                      <div>
                        <p className="font-semibold text-gray-900">{check.title}</p>
                        <p className="text-sm text-gray-600">{check.detail}</p>
                      </div>
                    </li>
                  )
                })}
              </ul>
            </div>

            {report.criteria.length > 0 && (
              <div className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-8 shadow-sm">
                <h2 className="text-xl font-bold text-gray-900">Criterion by criterion</h2>
                <p className="mt-1 text-sm text-gray-500">Matched on the key words in each criterion. A mention isn&apos;t the same as evidence, so check each covered one has a real example.</p>
                <ul className="mt-4 divide-y divide-gray-100">
                  {report.criteria.map((c, i) => (
                    <li key={`${i}-${c.text}`} className="py-3 flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4">
                      <span className={`self-start shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${CRITERION_STYLE[c.status]}`}>{CRITERION_LABEL[c.status]}</span>
                      <div className="min-w-0">
                        <p className="text-sm text-gray-900">{c.text}</p>
                        {c.status !== 'covered' && c.missing.length > 0 && (
                          <p className="text-xs text-gray-500 mt-0.5">Not found: {c.missing.join(', ')}</p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-xs text-gray-500">
              This checks the things shortlisters look for, using simple rules. It can&apos;t judge how good your examples are, so read your statement once more as a shortlister would.
            </p>
          </div>
        )}
      </section>

      <section className="container mx-auto px-4 pb-14">
        <div className="max-w-3xl mx-auto grid gap-5 md:grid-cols-2">
          <div className="rounded-2xl border border-primary-100 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900">Guides</h2>
            <ul className="mt-3 space-y-2 text-sm">
              <li><Link href="/blog/nhs-band-5-supporting-statement" className="text-primary-600 font-medium hover:underline">How to write an NHS Band 5 supporting statement</Link></li>
              <li><Link href="/blog/civil-service-behaviours-by-grade" className="text-primary-600 font-medium hover:underline">Civil Service behaviours by grade</Link></li>
            </ul>
          </div>
          <div className="rounded-2xl bg-gradient-to-br from-primary-600 to-primary-800 p-6 text-white">
            <h2 className="text-lg font-semibold">Then fill in the rest of the form</h2>
            <p className="mt-2 text-sm text-primary-100">
              Your work history, qualifications, referees and equality questions are the same on every NHS Jobs, Trac and Civil Service form. CVCircuit fills them in from your Career Bank.
            </p>
            <div className="mt-4 hidden md:block">
              <StoreButton placement="statement_checker" light />
            </div>
            <DesktopLinkForm placement="statement_checker" light className="mt-4" />
          </div>
        </div>
      </section>
    </LandingShell>
  )
}
